import { CoinMarketData } from '../types';

export const coinIdToCoinMap = new Map<string, CoinMarketData>();
export const coinsHistoricalDataMap = new Map<string, any[]>();
let top_coins_history, coin_markets, latest_update;
let dataVersion = '';

// Data files are versioned by the last update time, so a new deploy is picked up
// immediately while unchanged files stay cached between visits.
export function dataUrl(path: string) {
    return dataVersion ? `data/${path}?v=${dataVersion}` : `data/${path}`;
}

export async function loadAllCoinsData() {
    // Always revalidate this small file: it tells us which data version to fetch
    latest_update = await fetch('data/latest_update.json', { cache: 'no-cache' }).then(r => r.json());
    const updatedAt = new Date(latest_update.lastUpdated).getTime();
    dataVersion = isNaN(updatedAt) ? '' : String(updatedAt);

    [top_coins_history, coin_markets] = await Promise.all([
        fetch(dataUrl('top_coins_history.csv')).then(r => r.text()),
        fetch(dataUrl('coin_markets.json')).then(r => r.json())
    ]);

    // Save coin mapping
    coin_markets.forEach((coin: CoinMarketData) => {
        coinIdToCoinMap.set(coin.id, coin);
    });

    // Parse the initial combined history data into the coinsData map

    const rows = top_coins_history.split('\n').slice(1);
    for (const row of rows) {
        const [dateStr, coinId, value] = row.split(',');
        if (!coinsHistoricalDataMap.has(coinId)) {
            coinsHistoricalDataMap.set(coinId, []);
        }
        const dataPoint = {
            date: new Date(dateStr),
            name: coinIdToCoinMap.get(coinId).symbol.toUpperCase(),
            value: parseFloat(value),
            icon: `/icons/${coinIdToCoinMap.get(coinId).imageName}`
        };
        if (dataPoint.date && !isNaN(dataPoint.value)) {
            coinsHistoricalDataMap.get(coinId).push(dataPoint);
        }
    }

    return { top_coins_history, coin_markets, latest_update, coinIdToCoinMap };
}

export async function parseCSVData(csvData, coinId, coinIdToCoinMap) {
    if (!csvData) return [];
    return csvData.split('\n')
        .slice(1) // Skip header
        .map(row => {
            const [dateStr, value, open] = row.split(',');
            return {
                date: new Date(dateStr),
                name: coinIdToCoinMap.get(coinId).id.toUpperCase(),
                value: parseFloat(value),
                icon: `/icons/${coinIdToCoinMap.get(coinId).imageName}`
            };
        })
        .filter(obj => !isNaN(obj.value) && obj.date);
}

export async function loadCoinHistory(coinId: string) {
    if (coinsHistoricalDataMap.has(coinId)) {
        return coinsHistoricalDataMap.get(coinId);
    }
    try {
        const response = await fetch(dataUrl(`history/${coinId}.csv`), {
            headers: { 'Content-Type': 'text/csv', 'Accept': 'text/csv' }
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await parseCSVData(await response.text(), coinId, coinIdToCoinMap);
        coinsHistoricalDataMap.set(coinId, data);
        return data;
    } catch (error) {
        console.error(`Error loading history for ${coinId}:`, error);
        return null;
    }
}

export function displayLatestUpdate(latest_update: any) {
    const lastUpdateSpan = document.querySelector('.last-update');
    if (lastUpdateSpan && latest_update) {
        const date = new Date(latest_update.lastUpdated);
        const formattedDate = date.toLocaleDateString(undefined, { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
        lastUpdateSpan.textContent = `Last updated: ${formattedDate}`;
    }
} 